import GoogleAnalytics from '@clearkit/icons/glyphs/GoogleAnalytics';
import Hubspot from '@clearkit/icons/glyphs/Hubspot';
import Marketo from '@clearkit/icons/glyphs/Marketo';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import Segment from '@clearkit/icons/glyphs/SegmentCompany';
import { Subscribe } from 'unstated';

import ActiveIntegrationsList from '~/components/ActiveIntegrationsList';
import IntegrationPickerCard from '~/components/IntegrationPickerCard';
import {
  IntegrationPickerLayout,
  IntegrationPickerRow,
} from '~/components/layouts/IntegrationPickerLayout';
import SidebarDrawer from '~/components/SidebarDrawer';
import UpgradeAccountForm from '~/components/UpgradeAccountForm';
import UserContainer from '~/containers/User';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import {
  useHasConversionsAccess,
  useHasLegacyPlan,
  useHasOperationsAccess,
} from '~/hooks/account/usePlatformAccess';
import Tile from '~/images/Tiles/Enrichment.svg';

import LegacyEnrichmentList from './LegacyEnrichmentList';

const EnrichmentList = () => {
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [upgradeAccountFeature, setUpgradeAccountFeature] = useState(null);

  function showUpgradeAccountForm(featureName) {
    setShowUpgradeForm(true);
    setUpgradeAccountFeature(featureName);
  }

  const hasHubspotFeatureFlag = useFeatureFlag('x_data_enrichment_hubspot');
  const hasMarketoFeatureFlag = useFeatureFlag('x_marketo_on_platform_beta');
  const hasLegacyPlan = useHasLegacyPlan();
  const hasOperationsAccess = useHasOperationsAccess();
  const hasConversionsAccess = useHasConversionsAccess();
  const hasBothAddons = hasConversionsAccess && hasOperationsAccess;
  const hasDataHealthFlag = useFeatureFlag('x_enrichment_health');

  if (hasLegacyPlan) {
    return <LegacyEnrichmentList />;
  }

  const hubspotUrl = hasHubspotFeatureFlag.enabled
    ? '/enrichment/hubspot'
    : 'hubspot.clearbit.com';
  const hubspotVariant = hasHubspotFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;

  const marketoUrl = hasMarketoFeatureFlag.enabled
    ? '/enrichment/marketo'
    : 'marketo.clearbit.com';

  const marketoVariant = hasMarketoFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;
  const CONVERSIONS = [
    {
      Icon: Segment,
      type: 'segment',
      bgClass: 'bg-segment',
      title: 'Segment',
      text: 'Enrich people and companies in Segment with Clearbit data.',
      tag: 'Convert Intent',
      product: 'google_analytics',
      url: 'segment.clearbit.com',
      variant: IntegrationPickerCard.Variants.external,
    },
    {
      Icon: GoogleAnalytics,
      type: 'google_analytics',
      bgClass: 'bg-yellow',
      title: 'Google Analytics',
      text:
        'Enrich people and companies in Google Analytics with Clearbit data.',
      tag: 'Convert Intent',
      product: 'google_analytics',
      url: '/settings/configuration/google',
    },
  ];

  const OPERATIONS = [
    {
      Icon: Salesforce,
      type: 'salesforce',
      bgClass: 'bg-salesforce',
      title: 'Salesforce',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Salesforce.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: '/enrichment/salesforce',
    },
    {
      Icon: Salesforce,
      type: 'pardot',
      iconFillOverride: 'text-salesforce',
      bgClass: 'bg-gray-0',
      title: 'Pardot',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Pardot.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',

      url: 'pardot.clearbit.com',
      variant: IntegrationPickerCard.Variants.external,
    },
    {
      Icon: Marketo,
      type: 'marketo',
      bgClass: 'bg-marketo',
      title: 'Marketo',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Marketo.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: marketoUrl,
      variant: marketoVariant,
    },
    {
      Icon: Hubspot,
      type: 'hubspot',
      bgClass: 'bg-hubspot',
      title: 'Hubspot',
      text:
        'Enrich and refresh new contacts, form submissions, or your entire contact database within HubSpot.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: hubspotUrl,
      variant: hubspotVariant,
    },
  ];

  const accesibleIntegrations = { title: '', cards: [] };
  const unaccesibleIntegrations = { title: '', cards: [] };

  function assignIntegrations(arr = [], hasAccess) {
    arr.forEach((item) => {
      if (hasAccess) {
        accesibleIntegrations.cards.push(item);
      } else {
        unaccesibleIntegrations.cards.push(item);
      }
    });
  }

  assignIntegrations(OPERATIONS, hasOperationsAccess);
  assignIntegrations(CONVERSIONS, hasConversionsAccess);

  if (hasBothAddons) {
    accesibleIntegrations.title = 'Convert Intent & Accelerate Revenue';
  } else if (hasConversionsAccess) {
    accesibleIntegrations.title = 'Convert Intent';
    unaccesibleIntegrations.title = 'Accelerate Revenue';
  } else {
    accesibleIntegrations.title = 'Accelerate Revenue';
    unaccesibleIntegrations.title = 'Convert Intent';
  }
  return (
    <Subscribe to={[UserContainer]}>
      {(userContainer) => {
        return (
          <IntegrationPickerLayout
            background="white"
            subtitle="Get rich firmographic data into the systems that matter most to your business"
            tile={<Tile />}
            title="Enrichment"
          >
            <div className="flex flex-col flex-grow">
              {hasDataHealthFlag.enabled && <ActiveIntegrationsList />}

              {accesibleIntegrations.cards.length > 0 && (
                <IntegrationPickerRow
                  subtitle="Select your desired integration to get started."
                  title={`${accesibleIntegrations.title} integrations`}
                >
                  {accesibleIntegrations.cards.map((integration) => {
                    const objectType = 'enrichment';
                    const objectAction = 'update';
                    const objectSubtype = integration.type;

                    const hasPermission = userContainer.hasPermissionTo({
                      objectAction,
                      objectType,
                      objectSubtype,
                    });

                    return (
                      <IntegrationPickerCard
                        container={userContainer}
                        key={integration.title}
                        {...integration}
                        hasAccess
                        hasPermission={hasPermission}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
              {unaccesibleIntegrations.cards.length > 0 && (
                <IntegrationPickerRow
                  secondary
                  subtitle="Select your desired integration to get started."
                  title={`${unaccesibleIntegrations.title} integrations`}
                >
                  {unaccesibleIntegrations.cards.map((integration) => {
                    return (
                      <IntegrationPickerCard
                        key={integration.title}
                        {...integration}
                        hasAccess={false}
                        setShowUpgradeForm={() =>
                          showUpgradeAccountForm(integration.product)
                        }
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
            </div>

            <SidebarDrawer
              onClose={() => {
                setShowUpgradeForm(false);
              }}
              open={showUpgradeForm}
            >
              <UpgradeAccountForm
                onClose={() => {
                  setShowUpgradeForm(false);
                }}
                product={upgradeAccountFeature}
              />
            </SidebarDrawer>
          </IntegrationPickerLayout>
        );
      }}
    </Subscribe>
  );
};

export default EnrichmentList;
